"use client"

import type React from "react"

import { Search, Settings } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { ScrollArea } from "@/components/ui/scroll-area"

import type { EditFiltersSectionDescriptor, EditFiltersSectionId } from "./filter-dialog-types"

interface EditFiltersDialogShellProps {
  activeSection: EditFiltersSectionId
  children?: React.ReactNode
  content: React.ReactNode
  description: string
  hideInactiveFilters: boolean
  isControlled: boolean
  isOpen: boolean
  isSubmitting: boolean
  onActiveSectionChange: (sectionId: EditFiltersSectionId) => void
  onHideInactiveFiltersChange: (checked: boolean) => void
  onOpenChange: (open: boolean) => void
  onSearchFiltersChange: (value: string) => void
  onSubmit: React.FormEventHandler<HTMLFormElement>
  searchFilters: string
  submitLabel: string
  title: string
  visibleSections: EditFiltersSectionDescriptor[]
}

export function EditFiltersDialogShell({
  activeSection,
  children,
  content,
  description,
  hideInactiveFilters,
  isControlled,
  isOpen,
  isSubmitting,
  onActiveSectionChange,
  onHideInactiveFiltersChange,
  onOpenChange,
  onSearchFiltersChange,
  onSubmit,
  searchFilters,
  submitLabel,
  title,
  visibleSections,
}: EditFiltersDialogShellProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      {children ? (
        <DialogTrigger asChild>{children}</DialogTrigger>
      ) : !isControlled ? (
        <DialogTrigger asChild>
          <Button variant="outline" className="gap-2 bg-transparent">
            <Settings className="h-4 w-4" />
            Edit Filters
          </Button>
        </DialogTrigger>
      ) : null}

      <DialogContent className="max-h-[90vh] max-w-6xl p-0">
        <form onSubmit={onSubmit}>
          <div className="flex h-[80vh]">
            <div className="w-64 border-r bg-muted/30">
              <div className="border-b p-4">
                <DialogHeader>
                  <DialogTitle className="text-lg">{title}</DialogTitle>
                  <DialogDescription>{description}</DialogDescription>
                </DialogHeader>
                <div className="mt-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
                    <Input
                      placeholder="Search filters"
                      value={searchFilters}
                      onChange={(event) => onSearchFiltersChange(event.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
              </div>

              <ScrollArea className="flex-1">
                <div className="p-2">
                  {visibleSections.map((section) => (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => onActiveSectionChange(section.id)}
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors ${
                        activeSection === section.id
                          ? "bg-teal-100 text-teal-900"
                          : "text-muted-foreground hover:bg-muted"
                      }`}
                    >
                      <section.icon className="h-4 w-4" />
                      <span className="text-sm font-medium">{section.label}</span>
                      {section.active ? <div className="ml-auto h-2 w-2 rounded-full bg-teal-600" /> : null}
                    </button>
                  ))}
                </div>
              </ScrollArea>

              <div className="border-t p-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="hide-inactive"
                    checked={hideInactiveFilters}
                    onCheckedChange={(checked) => onHideInactiveFiltersChange(Boolean(checked))}
                  />
                  <Label htmlFor="hide-inactive" className="text-sm">
                    Hide inactive filters
                  </Label>
                </div>
              </div>
            </div>

            <div className="flex flex-1 flex-col">
              <div className="flex items-center justify-between border-b p-6">
                <div className="space-y-1">
                  <h3 className="text-lg font-semibold">{title}</h3>
                  <p className="text-sm text-muted-foreground">{description}</p>
                </div>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={isSubmitting}>
                  {isSubmitting ? "Saving..." : submitLabel}
                </Button>
              </div>

              <ScrollArea className="flex-1 p-6">{content}</ScrollArea>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
